import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/StatusBadge';
import { getRecord, openRecord, uploadSignature, submitSignature, uploadPhoto, getFileUrl, formatApiError } from '../../utils/api';
import SignatureCanvas from 'react-signature-canvas';
import { ArrowLeft, Camera, Eraser, Send, CheckCircle, FileText, Loader } from 'lucide-react';

export default function DriverJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sigRef = useRef(null);

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [receiverName, setReceiverName] = useState('');
  const [receiverNotes, setReceiverNotes] = useState('');
  const [photoUrls, setPhotoUrls] = useState([]);
  const [sigEmpty, setSigEmpty] = useState(true);

  const loadRecord = useCallback(async () => {
    try {
      const { data } = await getRecord(id);
      setRecord(data);
      // Auto-open record
      if (data.status === 'allocated') {
        try { await openRecord(id); } catch (openErr) { console.warn('Auto-open skipped:', openErr.message); }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadRecord(); }, [loadRecord]);

  const clearSignature = () => {
    sigRef.current?.clear();
    setSigEmpty(true);
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { data } = await uploadPhoto(file);
      setPhotoUrls([...photoUrls, data.url]);
    } catch (err) {
      console.error('Photo upload failed:', err);
    }
  };

  const handleSubmit = async () => {
    if (!receiverName.trim()) { setError('Receiver name is required'); return; }
    if (sigEmpty || sigRef.current?.isEmpty()) { setError('Signature is required'); return; }

    setError('');
    setSubmitting(true);

    try {
      // Convert signature canvas to blob
      const sigDataUrl = sigRef.current.toDataURL('image/png');
      const sigBlob = await (await fetch(sigDataUrl)).blob();
      const sigFile = new File([sigBlob], 'signature.png', { type: 'image/png' });

      // Upload signature
      const { data: sigData } = await uploadSignature(sigFile);

      // Submit signature
      await submitSignature(id, {
        receiver_name: receiverName.trim(),
        receiver_notes: receiverNotes.trim() || null,
        signature_image_url: sigData.url,
        photo_urls: photoUrls
      });

      setSubmitted(true);
      setTimeout(() => navigate('/driver/jobs'), 3000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const r = record;
  const isTS = r?.document_type === 'trip_sheet';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0D9488] border-t-transparent"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-8">
        <div className="text-center" data-testid="submission-success">
          <CheckCircle className="mx-auto mb-4 text-[#10B981]" size={64} />
          <h2 className="text-2xl font-bold text-[#1A1A2E] mb-2">Proof Submitted!</h2>
          <p className="text-[#6B7280]">Delivery proof has been recorded and emailed.</p>
          <p className="text-sm text-[#94A3B8] mt-2">Returning to My Jobs...</p>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-8">
        <p className="text-[#6B7280]">Record not found.</p>
      </div>
    );
  }

  // Already signed/completed
  if (['signed', 'completed', 'email_exception', 'archived'].includes(r.status)) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
            <button onClick={() => navigate('/driver/jobs')} className="flex items-center gap-2 text-[#6B7280]"><ArrowLeft size={20} /> Back</button>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-6 py-8 text-center">
          <CheckCircle className="mx-auto mb-4 text-[#10B981]" size={48} />
          <h2 className="text-xl font-bold text-[#1A1A2E]">This record has been completed</h2>
          <StatusBadge status={r.status} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="driver-job-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/driver/jobs')} className="flex items-center gap-2 text-[#6B7280] hover:text-[#1A1A2E]" data-testid="back-button">
            <ArrowLeft size={20} /> Back
          </button>
          <div className="text-center">
            <span className="text-lg font-bold text-[#1A1A2E]">
              {isTS ? r.trip_sheet_fields?.trip_sheet_no : r.waybill_fields?.waybill_no}
            </span>
          </div>
          <StatusBadge status={r.status} />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - PDF Viewer */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
              <FileText size={16} className="text-[#6B7280]" />
              <span className="text-sm font-semibold text-[#1A1A2E]">Source Document</span>
            </div>
            <div className="p-4">
              {r.source_pdf_url ? (
                <div>
                  <object
                    data={getFileUrl(r.source_pdf_url)}
                    type="application/pdf"
                    className="w-full border border-gray-200 rounded-lg"
                    style={{ height: '500px' }}
                    data-testid="pdf-viewer"
                  >
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <FileText className="mb-3 text-[#94A3B8]" size={40} />
                      <p className="text-sm text-[#6B7280] mb-3">PDF preview not available in this browser</p>
                      <a
                        href={getFileUrl(r.source_pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-5 py-2.5 bg-[#0D9488] text-white rounded-lg font-semibold text-sm"
                      >
                        Open PDF
                      </a>
                    </div>
                  </object>
                  <a
                    href={getFileUrl(r.source_pdf_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-sm text-[#0D9488] font-medium"
                  >
                    Open PDF in new tab
                  </a>
                </div>
              ) : (
                <div className="text-center py-16 text-[#6B7280]">
                  <FileText className="mx-auto mb-3 text-[#94A3B8]" size={40} />
                  <p>No PDF attached</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Signature Form */}
          <div className="space-y-4">
            {/* Record Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-bold text-[#1A1A2E] mb-3">Job Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#6B7280]">3rd Party Agent</span><span className="font-medium text-[#1A1A2E]">{r.third_party_agent}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Dispatching Hub</span><span className="font-medium text-[#1A1A2E]">{r.dispatching_hub}</span></div>
                {isTS && r.trip_sheet_fields && <>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Trip Sheet No.</span><span className="font-medium text-[#1A1A2E]">{r.trip_sheet_fields.trip_sheet_no}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">No. of Waybills</span><span className="font-medium text-[#1A1A2E]">{r.trip_sheet_fields.no_of_waybills}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">No. of Items</span><span className="font-medium text-[#1A1A2E]">{r.trip_sheet_fields.no_of_items}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Total Weight</span><span className="font-medium text-[#1A1A2E]">{r.trip_sheet_fields.total_weight} kg</span></div>
                </>}
                {!isTS && r.waybill_fields && <>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Waybill No.</span><span className="font-medium text-[#1A1A2E]">{r.waybill_fields.waybill_no}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Description</span><span className="font-medium text-[#1A1A2E]">{r.waybill_fields.description}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Weight</span><span className="font-medium text-[#1A1A2E]">{r.waybill_fields.weight} kg</span></div>
                </>}
                {r.notes && <div className="border-t border-gray-100 pt-2"><span className="text-[#6B7280]">Notes: </span><span className="text-[#1A1A2E]">{r.notes}</span></div>}
              </div>
            </div>

            {/* Receiver Details Form */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">Receiver Details</h3>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4" data-testid="submit-error">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5">Name of Receiver *</label>
                  <input
                    type="text"
                    value={receiverName}
                    onChange={e => setReceiverName(e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                    placeholder="Enter receiver's full name"
                    data-testid="receiver-name-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5">Notes / Discrepancies</label>
                  <textarea
                    value={receiverNotes}
                    onChange={e => setReceiverNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                    placeholder="Any shortages, damages, count issues..."
                    data-testid="receiver-notes-input"
                  />
                </div>
              </div>
            </div>

            {/* Signature Pad */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-[#1A1A2E]">Signature *</h3>
                <button
                  onClick={clearSignature}
                  className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#EF4444]"
                  data-testid="clear-signature-button"
                >
                  <Eraser size={14} /> Clear
                </button>
              </div>
              <div className="border-2 border-gray-300 rounded-lg bg-white" style={{ touchAction: 'none' }}>
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#1A1A2E"
                  backgroundColor="white"
                  canvasProps={{
                    className: 'w-full',
                    style: { width: '100%', height: '200px' },
                    'data-testid': 'signature-canvas'
                  }}
                  onBegin={() => setSigEmpty(false)}
                />
              </div>
              <p className="text-xs text-[#94A3B8] mt-2">Sign above using your finger or stylus</p>
            </div>

            {/* Photo Capture */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-bold text-[#1A1A2E] mb-3">Photo (Optional)</h3>
              <label className="flex items-center justify-center gap-2 w-full h-14 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0D9488] transition-colors text-[#6B7280]">
                <Camera size={20} />
                <span className="text-sm font-medium">Capture Photo</span>
                <input type="file" accept="image/*" capture="environment" onChange={handlePhotoCapture} className="hidden" data-testid="photo-capture-input" />
              </label>
              {photoUrls.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {photoUrls.map((url) => (
                    <img key={url} src={getFileUrl(url)} alt="Photo" className="w-16 h-16 object-cover border border-gray-200 rounded-lg" />
                  ))}
                </div>
              )}
            </div>

            {/* System Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-[#6B7280]">
              <div className="flex justify-between"><span>Driver</span><span className="font-medium text-[#1A1A2E]">{user?.name}</span></div>
              <div className="flex justify-between mt-1"><span>Date</span><span className="font-medium text-[#1A1A2E]">{new Date().toLocaleDateString()}</span></div>
              <div className="flex justify-between mt-1"><span>Time</span><span className="font-medium text-[#1A1A2E]">{new Date().toLocaleTimeString()}</span></div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full h-14 bg-[#10B981] hover:bg-[#059669] text-white rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="driver-job-submit-button"
            >
              {submitting ? (
                <><Loader className="animate-spin" size={20} /> Submitting...</>
              ) : (
                <><Send size={20} /> Submit Delivery Proof</>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
